import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  animatableClips,
  applyAnimation,
  applyProcessedFootage,
  attachAnimationJob,
  reconcileSceneJobs,
  revertToOriginalFootage,
  revertToStill,
  type EditorClip,
  type EditorState,
} from '@rehub/propertycut-timeline';
import type { PropertyCutAsset, RenderFormat } from '../../types';
import { fetchAssets, getFreshSignedUrl } from '../../services/supabase';
import {
  fetchJobQuote,
  frameStill,
  isActiveJob,
  JobRequestError,
  newIdempotencyKey,
  pollJobs,
  startSceneAnimation,
  startWalkthrough,
  uploadFramedStill,
  type JobQuote,
  type PropertyCutJob,
} from '../../services/jobs';

const POLL_MS = 6000;

export interface BatchProgress {
  running: boolean;
  done: number;
  total: number;
  summary: string | null;
}

export interface WalkthroughClip {
  clipId: string;
  asset: PropertyCutAsset;
}

export interface SceneTools {
  format: RenderFormat;
  quote: JobQuote | null;
  quoteError: string | null;
  jobs: PropertyCutJob[];
  pending: Record<string, boolean>;
  errors: Record<string, string>;
  batch: BatchProgress;
  eligiblePhotos: EditorClip[];
  walkthroughClips: WalkthroughClip[];
  walkthroughJob: PropertyCutJob | null;
  walkthroughBusy: boolean;
  walkthroughError: string | null;
  /** Clips that could switch to footage from the latest finished walkthrough job. */
  walkthroughUnapplied: number;
  latestSceneJob: (clipId: string) => PropertyCutJob | null;
  assetById: (assetId: string) => PropertyCutAsset | null;
  processedFor: (assetId: string) => PropertyCutAsset | null;
  onAnimate: (clipId: string) => Promise<void>;
  onAnimateAll: () => Promise<void>;
  onUseAnimation: (clipId: string) => void;
  onRevertAnimation: (clipId: string) => void;
  onStartWalkthrough: (assetIds: string[]) => Promise<void>;
  onApplyWalkthrough: () => void;
  onUseProcessed: (clipId: string) => void;
  onUseOriginal: (clipId: string) => void;
}

interface Options {
  userId: string;
  projectId: string;
  format: RenderFormat;
  state: EditorState;
  assets: PropertyCutAsset[];
  setAssets: React.Dispatch<React.SetStateAction<PropertyCutAsset[]>>;
  edit: (recipe: (current: EditorState) => EditorState) => void;
  /** Apply a change without an undo step (server results, not user edits). */
  sync: (recipe: (current: EditorState) => EditorState) => void;
  onCredits: (credits: number) => void;
}

function errorText(err: unknown, credits: number | null): string {
  if (err instanceof JobRequestError) {
    if (err.code === 'INSUFFICIENT_CREDITS') return 'Not enough credits.';
    if (err.code === 'PRICE_CHANGED') {
      return `The price is now ${err.credits ?? credits ?? '?'} credits. Check it and try again.`;
    }
    if (err.code === 'MODERATION_REJECTED') return 'This file can’t be used because it failed our content check.';
    if (err.code === 'MODERATION_UNSUPPORTED') return 'This file type can’t be checked. Use a JPEG, PNG or WebP image.';
    if (err.code === 'MODERATION_UNAVAILABLE') return 'The content check is busy. You have not been charged; try again shortly.';
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

function isOriginalFootage(asset: PropertyCutAsset): boolean {
  return asset.kind === 'video' && asset.source !== 'imagemotion' && asset.source !== 'videomotion';
}

export function useSceneJobs(opts: Options): SceneTools {
  const { userId, projectId, format, state, assets, setAssets, edit, sync, onCredits } = opts;
  const [jobs, setJobs] = useState<PropertyCutJob[]>([]);
  const [quote, setQuote] = useState<JobQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [batch, setBatch] = useState<BatchProgress>({ running: false, done: 0, total: 0, summary: null });
  const [walkthroughBusy, setWalkthroughBusy] = useState(false);
  const [walkthroughError, setWalkthroughError] = useState<string | null>(null);
  const requestedAssets = useRef(new Set<string>());
  const statuses = useRef(new Map<string, string>());
  const latest = useRef({ state, assets, quote });
  latest.current = { state, assets, quote };

  const refreshQuote = useCallback(async () => {
    try {
      const next = await fetchJobQuote(projectId);
      setQuote(next);
      setQuoteError(null);
      onCredits(next.balance);
      return next;
    } catch (err) {
      setQuoteError(errorText(err, null));
      return null;
    }
  }, [onCredits, projectId]);

  const mergeJob = useCallback((job: PropertyCutJob) => {
    setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)]);
  }, []);

  useEffect(() => {
    void refreshQuote();
    pollJobs(projectId).then(setJobs).catch(() => undefined);
  }, [projectId, refreshQuote]);

  const anyActive = jobs.some(isActiveJob);
  useEffect(() => {
    if (!anyActive) return;
    const timer = window.setInterval(() => {
      pollJobs(projectId).then(setJobs).catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [anyActive, projectId]);

  // A failed job is refunded on the server, so re-read the balance when one fails.
  useEffect(() => {
    let refunded = false;
    for (const job of jobs) {
      const before = statuses.current.get(job.id);
      if (before && before !== job.status && job.status === 'failed') refunded = true;
      statuses.current.set(job.id, job.status);
    }
    if (refunded) void refreshQuote();
  }, [jobs, refreshQuote]);

  // Finished jobs add clips to the media library; load the new rows once.
  useEffect(() => {
    const known = new Set(assets.map((asset) => asset.id));
    const missing = jobs
      .filter((job) => job.status === 'ready')
      .flatMap((job) => job.result_asset_ids)
      .filter((id) => !known.has(id) && !requestedAssets.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requestedAssets.current.add(id));
    void (async () => {
      const rows = await fetchAssets(projectId);
      const fresh = await Promise.all(
        rows
          .filter((row) => missing.includes(row.id))
          .map(async (row) => ({ ...row, signed_url: await getFreshSignedUrl(row.storage_path, row.signed_url) })),
      );
      if (fresh.length === 0) return;
      setAssets((current) => {
        const have = new Set(current.map((asset) => asset.id));
        return [...current, ...fresh.filter((row) => !have.has(row.id))];
      });
    })().catch(() => undefined);
  }, [assets, jobs, projectId, setAssets]);

  useEffect(() => {
    const ids = new Set(assets.map((asset) => asset.id));
    sync((current) => reconcileSceneJobs(current, jobs, ids));
  }, [assets, jobs, sync]);

  const busyJobIds = useMemo(
    () => new Set(jobs.filter(isActiveJob).map((job) => job.id)),
    [jobs],
  );
  const eligiblePhotos = useMemo(
    () => animatableClips(state, assets, busyJobIds),
    [assets, busyJobIds, state],
  );

  const latestSceneJob = useCallback((clipId: string) => {
    let found: PropertyCutJob | null = null;
    for (const job of jobs) {
      if (job.kind !== 'imagemotion_scene' || job.scene_id !== clipId) continue;
      if (!found || job.created_at > found.created_at) found = job;
    }
    return found;
  }, [jobs]);

  const animateOne = useCallback(async (clip: EditorClip, credits: number, key: string) => {
    const { assets: currentAssets } = latest.current;
    const photo = currentAssets.find((asset) => asset.id === (clip.sourceAssetId ?? clip.assetId));
    if (!photo?.signed_url) throw new Error('This photo is not available.');
    const framing = clip.sourceAssetId && clip.animation?.still ? clip.animation.still : clip;
    const blob = await frameStill(photo.signed_url, format, framing.offsetX, framing.offsetY);
    const framedPath = await uploadFramedStill(userId, projectId, clip.id, blob);
    const job = await startSceneAnimation({
      projectId,
      sceneId: clip.id,
      sourceAssetId: photo.id,
      framedPath,
      expectedCredits: credits,
      idempotencyKey: key,
    });
    mergeJob(job);
    if (job.status === 'failed') throw new Error(job.error_message || 'The animation could not be started.');
    edit((current) => attachAnimationJob(current, clip.id, job.id));
    return job;
  }, [edit, format, mergeJob, projectId, userId]);

  const onAnimate = useCallback(async (clipId: string) => {
    const { state: currentState, quote: currentQuote } = latest.current;
    const clip = currentState.clips.find((item) => item.id === clipId);
    const credits = currentQuote?.scene.credits;
    if (!clip || credits === null || credits === undefined || pending[clipId]) return;
    setPending((current) => ({ ...current, [clipId]: true }));
    setErrors(({ [clipId]: _drop, ...rest }) => rest);
    try {
      await animateOne(clip, credits, newIdempotencyKey(`scene:${clipId}`));
    } catch (err) {
      setErrors((current) => ({ ...current, [clipId]: errorText(err, credits) }));
    } finally {
      setPending(({ [clipId]: _drop, ...rest }) => rest);
      void refreshQuote();
    }
  }, [animateOne, pending, refreshQuote]);

  const onAnimateAll = useCallback(async () => {
    const credits = latest.current.quote?.scene.credits;
    if (credits === null || credits === undefined || batch.running) return;
    const targets = [...eligiblePhotos];
    if (targets.length === 0) return;
    const batchKey = newIdempotencyKey('batch');
    setBatch({ running: true, done: 0, total: targets.length, summary: null });
    let started = 0;
    const failures: string[] = [];
    let stopped = false;
    for (let index = 0; index < targets.length; index += 1) {
      const clip = targets[index];
      setPending((current) => ({ ...current, [clip.id]: true }));
      try {
        await animateOne(clip, credits, `${batchKey}:${clip.id}`);
        started += 1;
      } catch (err) {
        const message = errorText(err, credits);
        setErrors((current) => ({ ...current, [clip.id]: message }));
        failures.push(message);
        if (err instanceof JobRequestError && (err.code === 'INSUFFICIENT_CREDITS' || err.code === 'PRICE_CHANGED')) {
          stopped = true;
        }
      } finally {
        setPending(({ [clip.id]: _drop, ...rest }) => rest);
        setBatch((current) => ({ ...current, done: index + 1 }));
      }
      if (stopped) break;
    }
    const skipped = targets.length - started - failures.length;
    const parts = [`${started} of ${targets.length} animations started.`];
    if (failures.length) parts.push(`${failures.length} could not start: ${failures[0]}`);
    if (skipped > 0) parts.push(`${skipped} not attempted.`);
    setBatch({ running: false, done: targets.length, total: targets.length, summary: parts.join(' ') });
    void refreshQuote();
  }, [animateOne, batch.running, eligiblePhotos, refreshQuote]);

  const onUseAnimation = useCallback((clipId: string) => {
    const clip = latest.current.state.clips.find((item) => item.id === clipId);
    const generated = latest.current.assets.find((asset) => asset.id === clip?.animation?.assetId);
    if (!clip || !generated) return;
    edit((current) => applyAnimation(current, clipId, generated.duration_seconds));
  }, [edit]);

  const onRevertAnimation = useCallback((clipId: string) => {
    edit((current) => revertToStill(current, clipId));
  }, [edit]);

  const walkthroughClips = useMemo(() => {
    const byId = new Map(assets.map((asset) => [asset.id, asset]));
    const seen = new Set<string>();
    const list: WalkthroughClip[] = [];
    for (const clip of state.clips) {
      const asset = byId.get(clip.assetId);
      if (clip.blank || clip.videoMotion || !asset || !isOriginalFootage(asset) || seen.has(asset.id)) continue;
      seen.add(asset.id);
      list.push({ clipId: clip.id, asset });
    }
    return list;
  }, [assets, state.clips]);

  const walkthroughJob = useMemo(() => {
    let found: PropertyCutJob | null = null;
    for (const job of jobs) {
      if (job.kind !== 'videomotion_walkthrough') continue;
      if (!found || job.created_at > found.created_at) found = job;
    }
    return found;
  }, [jobs]);

  const assetById = useCallback(
    (assetId: string) => assets.find((asset) => asset.id === assetId) ?? null,
    [assets],
  );

  const processedFor = useCallback((assetId: string) => {
    const byId = new Map(assets.map((asset) => [asset.id, asset]));
    const ready = jobs
      .filter((job) => job.kind === 'videomotion_walkthrough' && job.status === 'ready')
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    for (const job of ready) {
      const index = job.source_asset_ids.indexOf(assetId);
      const result = index >= 0 ? byId.get(job.result_asset_ids[index]) : undefined;
      if (result) return result;
    }
    return null;
  }, [assets, jobs]);

  const walkthroughUnapplied = useMemo(() => {
    if (walkthroughJob?.status !== 'ready') return 0;
    return state.clips.filter((clip) => (
      !clip.videoMotion && walkthroughJob.source_asset_ids.includes(clip.assetId) && processedFor(clip.assetId)
    )).length;
  }, [processedFor, state.clips, walkthroughJob]);

  const onStartWalkthrough = useCallback(async (assetIds: string[]) => {
    const credits = latest.current.quote?.walkthrough.credits;
    if (credits == null || walkthroughBusy || assetIds.length === 0) return;
    setWalkthroughBusy(true);
    setWalkthroughError(null);
    try {
      const job = await startWalkthrough({
        projectId,
        assetIds,
        expectedCredits: credits,
        idempotencyKey: newIdempotencyKey('walkthrough'),
      });
      mergeJob(job);
    } catch (err) {
      setWalkthroughError(errorText(err, credits));
    } finally {
      setWalkthroughBusy(false);
      void refreshQuote();
    }
  }, [mergeJob, projectId, refreshQuote, walkthroughBusy]);

  const footageSwap = useCallback((current: EditorState, clip: EditorClip): EditorState => {
    const processed = processedFor(clip.assetId);
    if (!processed || clip.videoMotion) return current;
    const job = jobs.find((item) => item.result_asset_ids.includes(processed.id));
    return applyProcessedFootage(current, clip.id, {
      assetId: processed.id,
      jobId: job?.id ?? '',
      durationSeconds: processed.duration_seconds,
    });
  }, [jobs, processedFor]);

  const onApplyWalkthrough = useCallback(() => {
    edit((current) => current.clips.reduce((next, clip) => footageSwap(next, clip), current));
  }, [edit, footageSwap]);

  const onUseProcessed = useCallback((clipId: string) => {
    edit((current) => {
      const clip = current.clips.find((item) => item.id === clipId);
      return clip ? footageSwap(current, clip) : current;
    });
  }, [edit, footageSwap]);

  const onUseOriginal = useCallback((clipId: string) => {
    edit((current) => {
      const clip = current.clips.find((item) => item.id === clipId);
      const source = latest.current.assets.find((asset) => asset.id === clip?.videoMotion?.sourceAssetId);
      return revertToOriginalFootage(current, clipId, source?.duration_seconds ?? null);
    });
  }, [edit]);

  return {
    format,
    quote,
    quoteError,
    jobs,
    pending,
    errors,
    batch,
    eligiblePhotos,
    walkthroughClips,
    walkthroughJob,
    walkthroughBusy,
    walkthroughError,
    walkthroughUnapplied,
    latestSceneJob,
    assetById,
    processedFor,
    onAnimate,
    onAnimateAll,
    onUseAnimation,
    onRevertAnimation,
    onStartWalkthrough,
    onApplyWalkthrough,
    onUseProcessed,
    onUseOriginal,
  };
}
